// client/src/components/media/SubtitleEditor.jsx
// In-app subtitle editor for videos.
// Allows adding timed subtitle cues, previewing on video, and saving as WebVTT.
// Pattern mirrors MediaEditor: fullscreen overlay, saves & uploads internally.

import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  FaTimes, FaCheck, FaPlus, FaTrash, FaPlay, FaPause,
  FaArrowLeft, FaClosedCaptioning,
} from 'react-icons/fa';
import './SubtitleEditor.css';

// ── helpers ──────────────────────────────────────────────────────────────────
function formatTime(seconds) {
  if (!seconds || seconds < 0) seconds = 0;
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  const ms = Math.floor((seconds % 1) * 1000);
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}.${String(ms).padStart(3, '0')}`;
}

function parseTime(str) {
  // Accepts HH:MM:SS.mmm or MM:SS.mmm or SS.mmm
  const parts = str.split(':');
  let h = 0, m = 0, s = 0;
  if (parts.length === 3) {
    h = parseInt(parts[0], 10) || 0;
    m = parseInt(parts[1], 10) || 0;
    s = parseFloat(parts[2]) || 0;
  } else if (parts.length === 2) {
    m = parseInt(parts[0], 10) || 0;
    s = parseFloat(parts[1]) || 0;
  } else {
    s = parseFloat(parts[0]) || 0;
  }
  return h * 3600 + m * 60 + s;
}

function generateVTT(cues) {
  let vtt = 'WEBVTT\n\n';
  cues.forEach((cue, i) => {
    vtt += `${i + 1}\n`;
    vtt += `${formatTime(cue.start)} --> ${formatTime(cue.end)}\n`;
    vtt += `${cue.text}\n\n`;
  });
  return vtt;
}

// ─────────────────────────────────────────────────────────────────────────────
export default function SubtitleEditor({
  src,
  tenantId,
  eventId = null,
  itemId = null,
  existingVttUrl = null,
  onSaved,
  onClose,
}) {
  const videoRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  // Subtitle cues: { id, start, end, text }
  const [cues, setCues] = useState([]);
  const [activeCueId, setActiveCueId] = useState(null);
  const [vttBlobUrl, setVttBlobUrl] = useState(null);

  const activeCue = cues.find(c => c.id === activeCueId);

  // ── Load existing VTT if provided ──────────────────────────────────────────
  useEffect(() => {
    if (!existingVttUrl) return;
    let alive = true;
    (async () => {
      try {
        const res = await fetch(existingVttUrl);
        if (!res.ok) return;
        const text = await res.text();
        const parsed = parseVTT(text);
        if (alive && parsed.length) setCues(parsed);
      } catch (e) {
        console.error('Failed to load existing VTT:', e);
      }
    })();
    return () => { alive = false; };
  }, [existingVttUrl]);

  function parseVTT(text) {
    const lines = text.split('\n');
    const parsed = [];
    let i = 0;
    // Skip header
    while (i < lines.length && !lines[i].includes('-->')) i++;
    while (i < lines.length) {
      const line = lines[i].trim();
      if (line.includes('-->')) {
        const [startStr, endStr] = line.split('-->').map(s => s.trim());
        const start = parseTime(startStr);
        const end = parseTime(endStr);
        i++;
        let textLines = [];
        while (i < lines.length && lines[i].trim() !== '') {
          textLines.push(lines[i].trim());
          i++;
        }
        parsed.push({
          id: Date.now() + parsed.length,
          start,
          end,
          text: textLines.join('\n'),
        });
      }
      i++;
    }
    return parsed;
  }

  // ── Regenerate VTT blob for preview whenever cues change ───────────────────
  useEffect(() => {
    if (vttBlobUrl) URL.revokeObjectURL(vttBlobUrl);
    if (cues.length === 0) { setVttBlobUrl(null); return; }
    const vttText = generateVTT(cues);
    const blob = new Blob([vttText], { type: 'text/vtt' });
    const url = URL.createObjectURL(blob);
    setVttBlobUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [cues]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Video time tracking ────────────────────────────────────────────────────
  useEffect(() => {
    const vid = videoRef.current;
    if (!vid) return;
    const onTime = () => setCurrentTime(vid.currentTime);
    const onMeta = () => setDuration(vid.duration || 0);
    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    vid.addEventListener('timeupdate', onTime);
    vid.addEventListener('loadedmetadata', onMeta);
    vid.addEventListener('play', onPlay);
    vid.addEventListener('pause', onPause);
    return () => {
      vid.removeEventListener('timeupdate', onTime);
      vid.removeEventListener('loadedmetadata', onMeta);
      vid.removeEventListener('play', onPlay);
      vid.removeEventListener('pause', onPause);
    };
  }, []);

  // Prevent body scroll
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  // ── Cue operations ─────────────────────────────────────────────────────────
  const addCue = () => {
    const start = currentTime;
    const end = Math.min(start + 3, duration || start + 3);
    const id = Date.now();
    setCues(prev => [...prev, { id, start, end, text: 'Subtitle text' }]);
    setActiveCueId(id);
  };

  const updateCue = (id, key, value) => {
    setCues(prev => prev.map(c => c.id === id ? { ...c, [key]: value } : c));
  };

  const removeCue = (id) => {
    setCues(prev => prev.filter(c => c.id !== id));
    if (activeCueId === id) setActiveCueId(null);
  };

  const setStartFromPlayhead = () => {
    if (activeCue) updateCue(activeCue.id, 'start', currentTime);
  };

  const setEndFromPlayhead = () => {
    if (activeCue) updateCue(activeCue.id, 'end', currentTime);
  };

  const togglePlay = () => {
    const vid = videoRef.current;
    if (!vid) return;
    if (vid.paused) vid.play();
    else vid.pause();
  };

  const seekTo = useCallback((time) => {
    const vid = videoRef.current;
    if (vid) vid.currentTime = time;
  }, []);

  // ── Save: generate VTT, upload, call onSaved ──────────────────────────────
  const handleSave = async () => {
    if (cues.length === 0) {
      // If no cues, save null to clear subtitles
      onSaved?.(null);
      onClose?.();
      return;
    }

    setBusy(true);
    setError('');

    try {
      const vttText = generateVTT(cues);
      const blob = new Blob([vttText], { type: 'text/vtt' });
      const fname = `subtitles-${Date.now()}.vtt`;

      const fd = new FormData();
      fd.append('file', new File([blob], fname, { type: 'text/vtt' }));
      if (tenantId) fd.append('tenantId', String(tenantId));
      if (eventId) fd.append('eventId', String(eventId));
      if (itemId) fd.append('itemId', String(itemId));

      const res = await fetch('/api/media/upload', { method: 'POST', body: fd, credentials: 'include' });
      if (!res.ok) throw new Error('Upload failed');
      const json = await res.json();
      if (!json.url) throw new Error('Upload response missing URL');

      onSaved?.(json.url);
      onClose?.();
    } catch (err) {
      console.error('SubtitleEditor save failed:', err);
      setError(err.message || 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  const handleReset = () => {
    setCues([]);
    setActiveCueId(null);
  };

  // ── Highlight current cue based on playback ────────────────────────────────
  const currentCue = cues.find(c => currentTime >= c.start && currentTime <= c.end);

  // ── render ─────────────────────────────────────────────────────────────────
  return (
    <div className="se-overlay">
      {/* header */}
      <div className="se-header">
        <button className="se-hdr-btn" onClick={onClose} disabled={busy}><FaTimes /></button>
        <span className="se-hdr-title"><FaClosedCaptioning /> Edit Subtitles</span>
        <div className="se-hdr-right">
          <button className="se-hdr-btn" onClick={handleReset} disabled={busy} title="Clear all">
            <FaArrowLeft /> Reset
          </button>
          <button className="se-hdr-btn se-hdr-save" onClick={handleSave} disabled={busy}>
            <FaCheck /> {busy ? 'Saving…' : 'Done'}
          </button>
        </div>
      </div>

      {error && <div className="se-error">{error}</div>}

      {/* video preview */}
      <div className="se-stage">
        <div className="se-video-wrap">
          <video
            ref={videoRef}
            src={src}
            crossOrigin="anonymous"
            playsInline
            style={{ width: '100%', maxHeight: '100%', display: 'block' }}
          >
            {vttBlobUrl && (
              <track
                key={vttBlobUrl}
                kind="subtitles"
                src={vttBlobUrl}
                srcLang="en"
                label="English"
                default
              />
            )}
          </video>
          {/* Subtitle overlay (fallback for browsers that don't render <track> in editors) */}
          {currentCue && (
            <div className="se-subtitle-overlay">
              {currentCue.text}
            </div>
          )}
        </div>
      </div>

      {/* transport controls */}
      <div className="se-transport">
        <button className="se-transport-btn" onClick={togglePlay}>
          {playing ? <FaPause /> : <FaPlay />}
        </button>
        <input
          type="range"
          className="se-scrubber"
          min={0}
          max={duration || 1}
          step={0.1}
          value={currentTime}
          onChange={(e) => seekTo(Number(e.target.value))}
        />
        <span className="se-time">
          {formatTime(currentTime).substring(3, 12)} / {formatTime(duration).substring(3, 12)}
        </span>
      </div>

      {/* cue editor panel */}
      <div className="se-panel">
        <div className="se-panel-header">
          <button className="se-btn-add" onClick={addCue}>
            <FaPlus /> Add Subtitle at {formatTime(currentTime).substring(3, 12)}
          </button>
          <span className="se-cue-count">{cues.length} cue{cues.length !== 1 ? 's' : ''}</span>
        </div>

        {/* Active cue editor */}
        {activeCue && (
          <div className="se-cue-editor">
            <div className="se-cue-row">
              <label>Text</label>
              <textarea
                className="se-cue-input"
                value={activeCue.text}
                onChange={(e) => updateCue(activeCue.id, 'text', e.target.value)}
                rows={2}
                placeholder="Subtitle text..."
              />
            </div>
            <div className="se-cue-row">
              <label>Start</label>
              <input
                type="text"
                className="se-time-input"
                value={formatTime(activeCue.start).substring(3, 12)}
                onChange={(e) => updateCue(activeCue.id, 'start', parseTime('00:' + e.target.value))}
              />
              <button className="se-btn-set" onClick={setStartFromPlayhead} title="Set to current time">
                Set
              </button>
              <label>End</label>
              <input
                type="text"
                className="se-time-input"
                value={formatTime(activeCue.end).substring(3, 12)}
                onChange={(e) => updateCue(activeCue.id, 'end', parseTime('00:' + e.target.value))}
              />
              <button className="se-btn-set" onClick={setEndFromPlayhead} title="Set to current time">
                Set
              </button>
              <button className="se-btn-del" onClick={() => removeCue(activeCue.id)}>
                <FaTrash />
              </button>
            </div>
          </div>
        )}

        {/* Cue list */}
        <div className="se-cue-list">
          {cues
            .slice()
            .sort((a, b) => a.start - b.start)
            .map((cue) => (
              <button
                key={cue.id}
                className={`se-cue-item${cue.id === activeCueId ? ' active' : ''}${cue === currentCue ? ' playing' : ''}`}
                onClick={() => { setActiveCueId(cue.id); seekTo(cue.start); }}
              >
                <span className="se-cue-time">
                  {formatTime(cue.start).substring(3, 12)} → {formatTime(cue.end).substring(3, 12)}
                </span>
                <span className="se-cue-text">{cue.text}</span>
              </button>
            ))}
        </div>
      </div>
    </div>
  );
}
